import type { PromptResult, PrivatePromptOptions } from './interfaces';

export enum EVENTS {
  PROMPT = 'prompt',
  FINISH_PROMPT = 'finish-prompt',
  FINISH_CONFIRM = 'finish-confirm',
}
type EventData = {
  [EVENTS.PROMPT]: PrivatePromptOptions;
  [EVENTS.FINISH_PROMPT]: PromptResult<string>;
  [EVENTS.FINISH_CONFIRM]: PromptResult<boolean>;
};

type Handler<E extends EVENTS> = (event: EventData[E]) => void;

export interface EventBusInterface {
  on<E extends EVENTS>(eventType: E, handler: Handler<E>): void;
  off<E extends EVENTS>(eventType: E, handler: Handler<E>): void;
  emit<E extends EVENTS>(eventType: E, event: EventData[E]): void;
}

type HandlerList<E extends EVENTS> = Handler<E>[];

type HandlerMap = {
  [E in EVENTS]?: HandlerList<E>;
};

export class EventBus implements EventBusInterface {
  protected allHandlers: HandlerMap = {};

  protected getHandlers<E extends EVENTS>(eventType: E) {
    return (this.allHandlers[eventType] as HandlerList<E> | undefined) || [];
  }

  public on<E extends EVENTS>(eventType: E, handler: Handler<E>) {
    const handlers = this.getHandlers(eventType);
    handlers.push(handler);
    this.allHandlers[eventType] = handlers as EventBus['allHandlers'][E];
  }
  public off<E extends EVENTS>(eventType: E, handler: Handler<E>) {
    const handlers = this.getHandlers(eventType);
    handlers.splice(handlers.indexOf(handler) >>> 0, 1);
  }
  public emit<E extends EVENTS>(eventType: E, event: EventData[E]) {
    const handlers = this.getHandlers(eventType);
    handlers.forEach((handler) => handler(event));
  }
}

export const globalEventBus = new EventBus();
